import { ThemeEvents } from '@theme/events';

class FreeShippingProgress extends HTMLElement {
  /** @type {() => void} */
  #onCartUpdate = this.#handleCartUpdate.bind(this);
  /** @type {(event: Event) => void} */
  #onCtaClick = this.#handleCtaClick.bind(this);

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.addEventListener('click', this.#onCtaClick);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.removeEventListener('click', this.#onCtaClick);
  }

  /**
   * @param {Event} event
   */
  #handleCtaClick(event) {
    const cta = event.target instanceof Element ? event.target.closest('[data-fsp-cta]') : null;
    if (!cta) return;

    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && 'close' in drawer && typeof drawer.close === 'function') {
      drawer.close();
    }
    document.documentElement.removeAttribute('scroll-lock');
  }

  /**
   * Drawer/cart bars are re-rendered by section morph — only sync the product-page bar.
   * @param {CustomEvent} event
   */
  #handleCartUpdate(event) {
    if (!this.classList.contains('free-shipping-progress--product')) return;

    const sections = event.detail?.data?.sections;
    if (sections) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.#syncFromDrawer());
      });
      return;
    }

    this.#syncFromCartJson();
  }

  /** Copy server-rendered totals from the cart drawer bar (same cart, correct | money format). */
  #syncFromDrawer() {
    const drawerBar = document.querySelector('free-shipping-progress.free-shipping-progress--drawer');
    if (!drawerBar) {
      this.#syncFromCartJson();
      return;
    }
    this.#copyStateFrom(drawerBar);
  }

  /** @param {HTMLElement} source */
  #copyStateFrom(source) {
    this.dataset.cartTotal = source.dataset.cartTotal || '';
    this.toggleAttribute('data-qualified', source.hasAttribute('data-qualified'));

    const fields = [
      ['[data-fsp-message]', 'textContent'],
      ['[data-fsp-current]', 'textContent'],
      ['[data-fsp-fill]', 'style.width'],
      ['[data-fsp-cta]', 'textContent'],
    ];

    for (const [selector, prop] of fields) {
      const from = source.querySelector(selector);
      const to = this.querySelector(selector);
      if (!from || !to) continue;

      if (prop === 'style.width' && from instanceof HTMLElement && to instanceof HTMLElement) {
        to.style.width = from.style.width;
      } else if (prop === 'textContent') {
        to.textContent = from.textContent;
      }
    }

    const cta = this.querySelector('[data-fsp-cta]');
    const sourceCta = source.querySelector('[data-fsp-cta]');
    if (cta instanceof HTMLAnchorElement && sourceCta instanceof HTMLAnchorElement) {
      cta.hidden = sourceCta.hidden;
      cta.href = sourceCta.href;
    } else if (cta instanceof HTMLAnchorElement) {
      cta.hidden = source.hasAttribute('data-qualified');
    }

    const track = this.querySelector('.free-shipping-progress__track');
    const sourceTrack = source.querySelector('.free-shipping-progress__track');
    if (track && sourceTrack) {
      track.setAttribute('aria-valuenow', sourceTrack.getAttribute('aria-valuenow') || '0');
    }
  }

  async #syncFromCartJson() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return;
      const cart = await response.json();
      this.dataset.cartTotal = String(cart.total_price);

      const threshold = Number(this.dataset.threshold || 0);
      if (!threshold) return;

      const remaining = Math.max(threshold - cart.total_price, 0);
      const progress = Math.min(Math.round((cart.total_price * 100) / threshold), 100);
      const qualified = cart.total_price >= threshold;
      const goalLabel = this.dataset.goalLabel || 'free shipping';

      this.toggleAttribute('data-qualified', qualified);

      const fill = this.querySelector('[data-fsp-fill]');
      if (fill instanceof HTMLElement) fill.style.width = `${progress}%`;

      const track = this.querySelector('.free-shipping-progress__track');
      if (track) track.setAttribute('aria-valuenow', String(cart.total_price));

      const message = this.querySelector('[data-fsp-message]');
      if (message) {
        message.textContent = qualified
          ? `You've unlocked ${goalLabel}!`
          : `You're ${this.#formatCents(remaining)} away from ${goalLabel}!`;
      }

      const current = this.querySelector('[data-fsp-current]');
      if (current) current.textContent = this.#formatCents(cart.total_price);

      const cta = this.querySelector('[data-fsp-cta]');
      if (cta instanceof HTMLAnchorElement) {
        cta.hidden = qualified;
        if (!qualified) cta.textContent = `Add ${this.#formatCents(remaining)} more`;
      }
    } catch {
      /* ignore */
    }
  }

  /** @param {number} cents */
  #formatCents(cents) {
    const template = this.querySelector('[data-fsp-current]');
    const threshold = this.querySelector('[data-fsp-threshold]');
    const reference = template?.textContent?.trim() || threshold?.textContent?.trim() || '';

    if (reference && reference.match(/\d/)) {
      const amount = (cents / 100).toFixed(2);
      const amountNoDecimals = String(Math.round(cents / 100));
      const numeric = reference.replace(/[\d.,]+/, '').trim();
      const sampleNumber = reference.match(/[\d.,]+/)?.[0] || '';

      if (sampleNumber.includes(',') && sampleNumber.includes('.')) {
        return reference.replace(/[\d.,]+/, amount.replace('.', ','));
      }
      if (sampleNumber.includes(',') && !sampleNumber.includes('.')) {
        return reference.replace(/[\d.,]+/, amount.replace('.', ','));
      }
      if (reference.includes(amountNoDecimals) && !reference.includes('.')) {
        return reference.replace(/[\d.,]+/, amountNoDecimals);
      }
      return reference.replace(/[\d.,]+/, amount);
    }

    return (cents / 100).toFixed(2);
  }
}

if (!customElements.get('free-shipping-progress')) {
  customElements.define('free-shipping-progress', FreeShippingProgress);
}
